import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Row from '../../../../../components/Row'
import TablePageNavigation from '../../../../../components/TablePageNavigation'
import WorkspaceUsersTable from '../dataManagement/WorkspaceUsersTable'
import { useWorkspaceHandlers } from '../../../../../services/actions/workspace/useWorkspaceHandlers'
import { calculateColumnWidth, oldBusinessPrices, oldProPrices } from '../../../../../utils'
import { useAuthStore } from '../../../../../stores/auth/useAuthStore'
import Text from '../../../../../components/Text'
import Column from '../../../../../components/Column'
import Button from '../../../../../components/Button'
import { AppRoutes, SidebarTypes } from '../../../../../interfaces/store/appStore'
import { useAppHandlers } from '../../../../../services/actions/app/useAppHandlers'
import { useNavigate } from 'react-router-dom'
import { IBillingPlanV2, IPrice } from '../../../../../interfaces/models/billingPlan'
import { useBillingHandlers } from '../../../../../services/actions/billing/useBillingHandlers'
import DropdownMenu from '../../../../../components/dropdown/DropdownMenu'
import DropdownItem from '../../../../../components/dropdown/DropdownItem'
import { styles } from '../../../../../styles/themes/style'
import { useWorkspaceUsersStore } from '../../../../../stores/workspaceUsers/useWorkspaceUsersStore'
import { useWorkspaceUsersActions } from '../../../../../stores/workspaceUsers/useWorkspaceUsersActions'
import useMediaQuery from '../../../../../components/useMediaQuery'
import ActionText from '../../../../../components/ActionText'
import SvgFragment from '../../../../../fragments/SvgFragment'
import { useAuthActions } from '../../../../../stores/auth/useAuthActions'
import { canOnlyBuyGrowth } from '../../../../billing/v2/pricingV2'

interface ITeamManagementProps { }


const TeamManagement: FunctionComponent<ITeamManagementProps> = () => {
    const tableRef = useRef<HTMLDivElement>(null)

    const navigate = useNavigate()
    const isSmall = useMediaQuery("(max-width: 768px)")

    const { getBillingPlansSettingsHandler } = useBillingHandlers()
    const { getWorkspaceUsersHandler, getInvitesHandlers, getWorkspaceUsersAndInvitesHandler, updateWorkspaceHandler } = useWorkspaceHandlers()
    const { openSidebar, setAddMembersModal, showErrorToast } = useAppHandlers()
    const { setWorkspace } = useAuthActions()

    const { store: { workspace, subscription, lifetime, workspaceOwner, workspaceAdmin, trial } } = useAuthStore()
    const { store: { workspaceUsers, workspaceUsersFilters, workspaceUsersParameters  } } = useWorkspaceUsersStore()

    const { setWorkspaceUsers, setWorkspaceUsersFilters, setWorkspaceUsersParameters} = useWorkspaceUsersActions()

    const [ selectedPrice, setSelectedPrice ] = useState<IPrice | undefined>(undefined)
    const [ selectedBillingPlan, setSelectedBillingPlan] = useState<IBillingPlanV2 | undefined>(undefined)
    const [ selectedOldBillingPlan, setSelectedOldBillingPlan] = useState<any>(undefined)
    const [ search, setSearch ] = useState<string>('all')
    const [ contentEditable, setContentEditable ] = useState<boolean>(false)
    const [ minContentEditableWidth, setMinContentEditableWidth ] = useState<string | undefined>(undefined)
    const [name, setName] = useState(workspace.name)

    useEffect(() => {
        if(workspaceOwner === false && workspaceAdmin === false) navigate(AppRoutes.SETTINGS_DATA_MANAGEMENT)
    }, [workspaceOwner, workspaceAdmin])


    const [columns, setColumns] = useState([
        { header: 'Name', key: ['nameColumn'], show: true, width: '160px', showSmall: true },
        { header: 'Email', key: ['emailColumn'], show: true, showSmall: true },
        { header: 'Team Role', key: ['teamRoleColum'], show: true, showSmall: true },
        { header: 'Last Active', key: ['lastActiveColumn'], show: true },
        { header: 'Notes', key: ['notesColumn'], show: true },
        { header: 'Status', key: ['statusColumn'], show: true, showSmall: true },
        { header: 'Actions', key: ['actions'], show: true, width: '48px', showSmall: true }
    ])

    const getAllWorkspaceUsers = useCallback(async () => {
        const {activeUsers, removedUsers, invites} = await getWorkspaceUsersAndInvitesHandler(workspaceUsersFilters);

        const allUsers = [
            ...activeUsers?.response.map((user: any) => (
                {
                    ...user,
                    type: 'workspaceUsers'
                }
            )).sort((a: any, b: any) => (b.owner ? 1 : 0) - (a.owner ? 1 : 0)),
            ...invites?.response.map((invite: any) => (
                {
                    id: invite._id,
                    email: invite.to.email,
                    resolved: invite.resolved,
                    active: invite.active,
                    type: 'workspaceInvites'
        
                }
            )),
            ...removedUsers?.response.map((user: any) => (
                {
                    ...user,
                    type: 'workspaceUsers'
                }
            ))
        ]

        setWorkspaceUsers(allUsers)
        setWorkspaceUsersParameters({...workspaceUsersParameters, total: activeUsers?.metadata?.total + invites?.metadata?.total + removedUsers?.metadata?.total})
    }, [workspaceUsersFilters])
    
    const getWorkspaceUsers = useCallback(async () => {
        const result = await getWorkspaceUsersHandler(workspaceUsersFilters);
        setWorkspaceUsers(result?.response.map((user: any) => (
            {
                ...user,
                type: 'workspaceUsers'
            }
        )).sort((a: any, b: any) => (b.owner ? 1 : 0) - (a.owner ? 1 : 0)))
        setWorkspaceUsersParameters(result.metadata)
    }, [workspaceUsersFilters])

    const getWorkspaceInvites = useCallback(async () => {
        const result = await getInvitesHandlers(workspaceUsersFilters);
        setWorkspaceUsers(result?.response.map((invite: any) => (
            {
                id: invite._id,
                email: invite.to.email,
                resolved: invite.resolved,
                active: invite.active,
                type: 'workspaceInvites'
    
            }
        )))
        setWorkspaceUsersParameters(result.metadata)
    }, [workspaceUsersFilters])

    const handlePriceAndPlan = useCallback(async () => {
        const billingPlans = await getBillingPlansSettingsHandler()
        let selectedPrice
        const selectedBillingPlan = billingPlans.find((plan: IBillingPlanV2) => {
            selectedPrice = plan.prices.find((price: IPrice) => price.priceId === subscription.priceId && price.productId === subscription.productId)
            if(selectedPrice) return true
        })
        const selectedOldBillingPlan = oldBusinessPrices().includes(subscription.priceId) || oldProPrices().includes(subscription.priceId)
        ? {} 
        : undefined;
        if(selectedBillingPlan) setSelectedBillingPlan(selectedBillingPlan)
        if(selectedOldBillingPlan) setSelectedOldBillingPlan(selectedOldBillingPlan)
        if(selectedPrice) setSelectedPrice(selectedPrice)
    },
    [])

    const handleAdditionalSeats = useCallback(async () => {
        // Canceled or on trial on a plan we no longer sell: buying seats means picking Growth on the pricing page
        if (selectedBillingPlan && selectedPrice && !canOnlyBuyGrowth(subscription, trial, selectedBillingPlan.name)) navigate(AppRoutes.BILLING_PLAN, { state: { changeSeats: true, preSelectedBillingPlan: selectedBillingPlan, preSelectedPrice: selectedPrice }})
        else navigate(AppRoutes.BILLING)
    },
    [subscription, trial, selectedPrice, selectedBillingPlan])

    const setWorkspaceUsersFiltersHandler = async (props: any) => {
        setWorkspaceUsers([])

        if(props) {
            setSearch(props)
            setWorkspaceUsersFilters({...workspaceUsersFilters, active: (props === 'active' || props === 'all') ? 'true' : 'false'})
        } else {
            setWorkspaceUsersFilters(workspaceUsersFilters)
        }
    }

    useEffect(() => {
        handlePriceAndPlan()
    }, [])

    useEffect(() => {
        if(search === 'all') getAllWorkspaceUsers();
        if(search === 'active' || search === 'removed') getWorkspaceUsers();
        if(search === 'invites') getWorkspaceInvites();
    }, [workspaceUsersFilters])

    useEffect(() => {
        const offsetWidth = tableRef.current?.offsetWidth
        const adjustedColumns = calculateColumnWidth(offsetWidth, columns, isSmall)
        setColumns(adjustedColumns)
    }, [workspaceUsers, isSmall])

    const handleSave = async () => {
        setContentEditable(false);
        const newValue = name.trim();
        if (newValue === workspace.name) return;

        if (newValue.length <= 2) {
            showErrorToast({message: "Name must be between 2 and 32 chars long"})
            return
        }

        setWorkspace({ ...workspace, name: newValue });
        await updateWorkspaceHandler({ name: newValue });
    };

    return <Container>
        <StyledRow padding='0 10px' spaceBetween>
            <StyledColumn>
                <Row alignItems gap='8px' style={{ height: '30px' }}>
                    {contentEditable ? (
                        <StyledInput
                            type="text"
                            value={name}
                            autoFocus
                            maxLength={32}
                            onChange={e => setName(e.target.value)}
                            onBlur={handleSave}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSave();
                                }
                            }}
                        />
                    ) : (
                        <Text $heading4>{workspace.name}</Text>
                    )}
                    <div
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            if(contentEditable) handleSave();
                            setContentEditable((prev) => !prev);
                        }}
                    >
                        <SvgFragment type="pen" />
                    </div>
                </Row>
                <Text>{`${workspace.seatsUsed} of ${trial ? 'unlimited' : subscription?.quantity + (lifetime ? 1 : 0)} seats used`}</Text>
            </StyledColumn>
            <StyledRow gap='10px' justifyContent='end'>
                <StyledDropdownMenu title={search === 'invites' ? 'Pending members' : search === 'all' ? 'All members' : search === 'active' ? 'Active members' : search === 'removed' && 'Removed members'}>
                    <DropdownItem onClickHandler={() => setWorkspaceUsersFiltersHandler('all')}>All</DropdownItem>
                    <DropdownItem onClickHandler={() => setWorkspaceUsersFiltersHandler('active')}>Active</DropdownItem>
                    <DropdownItem onClickHandler={() => setWorkspaceUsersFiltersHandler('invites')}>Pending</DropdownItem>
                    <DropdownItem onClickHandler={() => setWorkspaceUsersFiltersHandler('removed')}>Removed</DropdownItem>
                </StyledDropdownMenu>
                <Button $type='light-blue' $bigButton onClickHandler={setAddMembersModal}>Add members</Button>
                <Button $type='blue' disabled={(!selectedBillingPlan && !selectedOldBillingPlan) || selectedBillingPlan?.name === 'Starter'} $bigButton onClickHandler={handleAdditionalSeats}>Buy seats</Button>
            </StyledRow>
        </StyledRow>

        <MobileContainer>
            <StyledColumnMobile>
                <StyledColumnMobile>
                    <StyledRowMobile>
                        <StyledColumnMobile>
                            <Text $heading4>{workspace.name}</Text>
                            <ActionText onClickHandler={() => openSidebar(SidebarTypes.WORKSPACE_CREATE, {workspace})}>Change Workspace Name</ActionText>    
                            <Text>{`${workspace.seatsUsed} of ${trial ? 'unlimited' : subscription?.quantity + (lifetime ? 1 : 0)} seats used`}</Text>
                        </StyledColumnMobile>
                        <Button $type='blue' disabled={(!selectedBillingPlan && !selectedOldBillingPlan?.name) || selectedBillingPlan?.name === 'Starter'} $bigButton onClickHandler={handleAdditionalSeats}>Buy seats</Button> 
                    </StyledRowMobile>
                </StyledColumnMobile>
            </StyledColumnMobile>
            <StyledColumnMobile marginTop='16px'>
            <StyledRowMobile>
                <StyledDropdownMenu title={search === 'invites' ? 'Pending members' : search === 'all' ? 'All members' : search === 'active' ? 'Active members' : search === 'removed' && 'Removed members'}>
                    <DropdownItem onClickHandler={() => setWorkspaceUsersFiltersHandler('all')}>All</DropdownItem>
                    <DropdownItem onClickHandler={() => setWorkspaceUsersFiltersHandler('active')}>Active</DropdownItem>
                    <DropdownItem onClickHandler={() => setWorkspaceUsersFiltersHandler('invites')}>Pending</DropdownItem>
                    <DropdownItem onClickHandler={() => setWorkspaceUsersFiltersHandler('removed')}>Removed</DropdownItem>
                </StyledDropdownMenu>
                <StyledTablePageNavigation 
                    page={workspaceUsersFilters.page} 
                    pageSize={workspaceUsersFilters.pageSize} 
                    totalCount={workspaceUsersParameters.total}
                    onPageChanged={(value: number) => setWorkspaceUsersFilters({...workspaceUsersFilters, page: value})}
                    onPageSizeChanged={(value: number) => setWorkspaceUsersFilters({...workspaceUsersFilters, pageSize: value})}
                />
                </StyledRowMobile>
            </StyledColumnMobile>
            
        </MobileContainer>

        <StyledRow padding='0 10px'>
            <StyledTablePageNavigation 
                    page={workspaceUsersFilters.page} 
                    pageSize={workspaceUsersFilters.pageSize} 
                    totalCount={workspaceUsersParameters.total}
                    onPageChanged={(value: number) => setWorkspaceUsersFilters({...workspaceUsersFilters, page: value})}
                    onPageSizeChanged={(value: number) => setWorkspaceUsersFilters({...workspaceUsersFilters, pageSize: value})}
            />
        </StyledRow>
        <WorkspaceUsersTable tableRef={tableRef} data={workspaceUsers} columns={columns} setColumns={setColumns} setWorkspaceUsersFiltersHandler={setWorkspaceUsersFiltersHandler} />
    </Container>
}
const Container = styled.div`
    width: 100%;
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    height: 574px;
    max-height: 574px;

    @media (max-width: 786px) {
        padding: 0px;
        align-items: start;
    }
`
const MobileContainer = styled.div`
    display: none;

    @media (max-width: 786px) {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
`
const StyledRowMobile = styled(Row)`
    display: none;
    @media (max-width: 786px) {
        display: flex;
        flex-direction: row;
        width: 100%;
    }
`
const StyledColumnMobile = styled(Column)<{ marginTop?: string }>`
    display: none;
    @media (max-width: 786px) {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
        margin-top: ${({marginTop}) => marginTop ? marginTop : '0px' };
    }
`     

const StyledRow = styled(Row)<{padding?: string, justifyContent?: string }>`
    width: 100%;
    
    ${({justifyContent}) => justifyContent && `justify-content: ${justifyContent};`}
    
    ${({padding}) => padding && `padding: ${padding};`}
    @media (max-width: 786px) {
        display: none;
    }
`
const StyledColumn = styled(Column)`
    width: 100%;

    @media (max-width: 786px) {
        display: none;
    }
`
const StyledDropdownMenu = styled(DropdownMenu)`
    height: 34px;
    width: 150px;
    background-color: ${styles.colors.primary100};

    & .dropdown-toggle {
        padding: 0 7px;
    }

    & svg {
        margin-left: auto;
    }

    @media (max-width: 786px) {
        width: auto;
    }
`
const StyledTablePageNavigation = styled(TablePageNavigation)`
    margin-left: auto;
`
const StyledInput = styled.input`
    font-family: NunitoSans;
    font-weight: 700;
    font-size: 20px;
    line-height: 24px;
    letter-spacing: 0px;
    border-bottom: 1px solid ${styles.colors.primary600};
    max-width: 300px;
    overflow: hidden;
    white-space: nowrap;
    background: transparent;
`
export default TeamManagement
